import { chain, isEmpty } from "lodash-es";
import { useCallback, useMemo } from "react";

import { authService } from "@/common/services/auth";
import { AppPermission, AuthorizationProfileDto } from "@/core/api/generated/v1";

import { useAppSelector } from "../redux";
import { useAuthenticationInfo } from "./useAuthenticationInfo";

const appAccessPermissions: AppPermission[] = [
  AppPermission.FleetAppAccess,
  AppPermission.FleetCustomerAppAccess,
  AppPermission.AdminAppAccess,
];

export interface AuthorizationInfo {
  /** Value to use in deps array to react on changes. */
  depsValue: any;
  isInited: boolean;
  isPermissionsReady: boolean;
  authorizationProfile?: AuthorizationProfileDto;
  userPermissions?: AppPermission[];
  userPermissionsMap: Record<AppPermission, boolean>;
  appAccessCount: number;
  hasFleetAppAccess: boolean;
  hasFleetCustomerAppAccess: boolean;
  hasAdminAppAccess: boolean;
  /** Checks user has all the required permissions. */
  hasPermissions: (permissions: AppPermission[]) => boolean;
  /** Checks user has any of the optional permissions (at least one). */
  hasPermissionsAny: (permissions: AppPermission[]) => boolean;
  hasPermissionsAllAndAny: (params: {
    permissions: AppPermission[] | null | undefined;
    permissionsAny: AppPermission[] | null | undefined;
  }) => boolean;
  hasPermissionsAllOrAny: (params: {
    permissions: AppPermission[] | null | undefined;
    permissionsAny: AppPermission[] | null | undefined;
  }) => boolean;
}

/** Returns authorization info for currently authenticated user. */
export const useAuthorizationInfo = (): AuthorizationInfo => {
  const { isAuthenticated } = useAuthenticationInfo();
  const authState = useAppSelector((x) => x.auth);
  const authorizationProfile = useAppSelector((x) => x.auth.authorizationProfile);

  const isPermissionsReady = useMemo(() => authService.isPermissionsReady(authState), [authState]);
  const userPermissions = authService.userPermissions;

  const userPermissionsMap = useMemo(
    () =>
      chain(userPermissions)
        .keyBy((x) => x)
        .mapValues((v, k) => true)
        .value() as Record<AppPermission, boolean>,
    [userPermissions],
  );

  const checkPermissions = useCallback(
    (params: {
      permissions: AppPermission[] | null | undefined;
      permissionsAny: AppPermission[] | null | undefined;
    }) => {
      const result = {
        hasPermissionsRequired: false,
        hasPermissionsAny: false,
      };

      if (isAuthenticated === undefined || isAuthenticated === false) {
        return result;
      }
      if (!isPermissionsReady || !userPermissions) {
        return result;
      }

      const hasPermissionsRequired =
        isEmpty(params.permissions) ||
        (params.permissions || []).every((x) => userPermissionsMap[x]);
      const hasPermissionsAny =
        isEmpty(params.permissionsAny) ||
        (params.permissionsAny || []).some((x) => userPermissionsMap[x]);

      return { hasPermissionsRequired, hasPermissionsAny };
    },
    [isAuthenticated, isPermissionsReady, userPermissions, userPermissionsMap],
  );

  return {
    depsValue: userPermissions,
    isInited: isPermissionsReady,
    isPermissionsReady,
    authorizationProfile,
    userPermissions,
    userPermissionsMap: userPermissionsMap,
    appAccessCount: (authService.userPermissions || []).filter((p) =>
      appAccessPermissions.includes(p),
    ).length,
    hasFleetAppAccess: authService.userPermissions?.includes(AppPermission.FleetAppAccess) || false,
    hasFleetCustomerAppAccess:
      authService.userPermissions?.includes(AppPermission.FleetCustomerAppAccess) || false,
    hasAdminAppAccess: authService.userPermissions?.includes(AppPermission.AdminAppAccess) || false,
    hasPermissions: (permissions: AppPermission[]) => authService.userHasPermissions(permissions),
    hasPermissionsAny: (permissions: AppPermission[]) =>
      authService.userHasPermissionsAny(permissions),
    hasPermissionsAllAndAny: (params: {
      permissions: AppPermission[] | null | undefined;
      permissionsAny: AppPermission[] | null | undefined;
    }) => {
      const checkResult = checkPermissions(params);
      const hasPermissions = checkResult.hasPermissionsRequired && checkResult.hasPermissionsAny;
      return hasPermissions;
    },
    hasPermissionsAllOrAny: (params: {
      permissions: AppPermission[] | null | undefined;
      permissionsAny: AppPermission[] | null | undefined;
    }) => {
      const checkResult = checkPermissions(params);
      const hasPermissions = checkResult.hasPermissionsRequired || checkResult.hasPermissionsAny;
      return hasPermissions;
    },
  };
};
